import React, { useEffect, useState } from "react";
import * as authHelpers from "./helper";
import * as LoginComponents from "./styledComponents/styledLogin";
import FeedbackAlert from "../extras/Alert";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useLocation, useNavigate } from "react-router-dom";
import { XHR_STATE } from "../../helpers/xhr";
import { Link } from "react-router-dom";
import { updateFeedback } from "../../redux/slices/AppState";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { CircularProgress } from "@mui/material";
import { getDefaultStaffLandingRoute } from "../../auth/permissions";

const metrics = [
  { label: "Orders Today", value: "128", delta: "+12%" },
  { label: "Avg. Turn Time", value: "38 min", delta: "-4 min" },
  { label: "Revenue Today", value: "₦246k", delta: "+8.3%" },
];

const pulseNodes = [
  { cx: "20%", cy: "30%", r: 2.5, delay: "0s" },
  { cx: "45%", cy: "18%", r: 1.8, delay: "0.4s" },
  { cx: "70%", cy: "40%", r: 3, delay: "0.8s" },
  { cx: "35%", cy: "60%", r: 2, delay: "1.2s" },
  { cx: "80%", cy: "65%", r: 1.5, delay: "0.2s" },
  { cx: "60%", cy: "78%", r: 2.2, delay: "1s" },
  { cx: "15%", cy: "72%", r: 1.6, delay: "0.6s" },
];

const Login = () => {
  const logo = require("../../assets/logo-crop.png");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { feedback, xhr } = useSelector((state: RootState) => state.App);
  const { status } = useSelector((state: RootState) => state.Auth);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    if (status === "authenticated") {
      navigate(getDefaultStaffLandingRoute());
    } else if (status === "verification-required") {
      navigate("/app/email-verification");
    }
  }, [navigate, status]);

  useEffect(() => {
    const state = location.state as { sessionExpired?: boolean } | null;
    if (state?.sessionExpired) {
      if (!feedback.show) {
        dispatch(
          updateFeedback({
            show: true,
            type: "error",
            message: "Your session expired. Please sign in again.",
          })
        );
      }
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [dispatch, feedback.show, location.pathname, location.state, navigate]);

  return (
    <LoginComponents.Container>
      <LoginComponents.BrandPanel aria-hidden="true">
        <LoginComponents.BrandImage />
        <LoginComponents.BrandOverlay />
        <LoginComponents.NetworkSvg preserveAspectRatio="none">
          <line
            x1="20%"
            y1="30%"
            x2="45%"
            y2="18%"
            stroke="#833c41"
            strokeWidth="0.5"
            opacity="0.4"
          />
          <line
            x1="45%"
            y1="18%"
            x2="70%"
            y2="40%"
            stroke="#833c41"
            strokeWidth="0.5"
            opacity="0.4"
          />
          <line
            x1="70%"
            y1="40%"
            x2="80%"
            y2="65%"
            stroke="#833c41"
            strokeWidth="0.5"
            opacity="0.4"
          />
          <line
            x1="70%"
            y1="40%"
            x2="60%"
            y2="78%"
            stroke="#833c41"
            strokeWidth="0.5"
            opacity="0.3"
          />
          <line
            x1="20%"
            y1="30%"
            x2="35%"
            y2="60%"
            stroke="#833c41"
            strokeWidth="0.5"
            opacity="0.3"
          />
          <line
            x1="35%"
            y1="60%"
            x2="60%"
            y2="78%"
            stroke="#833c41"
            strokeWidth="0.5"
            opacity="0.3"
          />
          <line
            x1="15%"
            y1="72%"
            x2="35%"
            y2="60%"
            stroke="#833c41"
            strokeWidth="0.5"
            opacity="0.25"
          />
          <line
            x1="80%"
            y1="65%"
            x2="60%"
            y2="78%"
            stroke="#833c41"
            strokeWidth="0.5"
            opacity="0.25"
          />
          {pulseNodes.map((node, index) => (
            <LoginComponents.PulseNode
              key={index}
              cx={node.cx}
              cy={node.cy}
              r={node.r}
              delay={node.delay}
              fill="#833c41"
              opacity="0.6"
            />
          ))}
        </LoginComponents.NetworkSvg>

        <LoginComponents.BrandContent>
          <LoginComponents.BrandLockup>
            <LoginComponents.LogoMark>
              <img src={logo} alt="" />
            </LoginComponents.LogoMark>
            <LoginComponents.Eyebrow>Menutender</LoginComponents.Eyebrow>
          </LoginComponents.BrandLockup>

          <LoginComponents.HeroCopy>
            <LoginComponents.AccentLine>
              <AutoAwesomeIcon sx={{ fontSize: 14 }} />
              <span>Hospitality operations dashboard</span>
            </LoginComponents.AccentLine>
            <LoginComponents.HeroTitle>
              The operating
              <br />
              <em>system</em> for your
              <br />
              restaurant floor.
            </LoginComponents.HeroTitle>
            <LoginComponents.HeroText>
              Manage menus, staff, segments, and live customer orders from one
              focused workspace built for hospitality teams.
            </LoginComponents.HeroText>
          </LoginComponents.HeroCopy>

          <LoginComponents.MetricsPanel>
            <LoginComponents.LiveStatus>
              Live — Main location
            </LoginComponents.LiveStatus>
            <LoginComponents.MetricsGrid>
              {metrics.map((metric) => (
                <div key={metric.label}>
                  <LoginComponents.MetricValue>
                    {metric.value}
                  </LoginComponents.MetricValue>
                  <LoginComponents.MetricMeta>
                    <strong>{metric.delta}</strong>
                    <span>{metric.label}</span>
                  </LoginComponents.MetricMeta>
                </div>
              ))}
            </LoginComponents.MetricsGrid>
          </LoginComponents.MetricsPanel>
        </LoginComponents.BrandContent>
      </LoginComponents.BrandPanel>

      <LoginComponents.FormPanel>
        <LoginComponents.FeedbackWrapper>
          <FeedbackAlert
            show={feedback.show}
            message={feedback.message}
            type={feedback.type}
            timeout={2000}
          />
        </LoginComponents.FeedbackWrapper>

        <LoginComponents.FormShell>
          <LoginComponents.MobileBrand>
            <LoginComponents.LogoMark>
              <img src={logo} alt="" />
            </LoginComponents.LogoMark>
            <LoginComponents.Eyebrow>Menutender</LoginComponents.Eyebrow>
          </LoginComponents.MobileBrand>

          <LoginComponents.FormHeader>
            <LoginComponents.Eyebrow>Admin access</LoginComponents.Eyebrow>
            <LoginComponents.FormTitle>Welcome back</LoginComponents.FormTitle>
            <LoginComponents.FormSubtitle>
              Sign in to your restaurant dashboard
            </LoginComponents.FormSubtitle>
          </LoginComponents.FormHeader>

          <LoginComponents.Form
            onSubmit={(e) =>
              authHelpers.loginSubmitHandler(e, credentials, dispatch)
            }
          >
            <LoginComponents.FieldGroup>
              <LoginComponents.Label htmlFor="email">
                Email
              </LoginComponents.Label>
              <LoginComponents.InputWrapper>
                <LoginComponents.InputField
                  id="email"
                  type="email"
                  autoComplete="email"
                  onChange={(e) => {
                    const lowerCaseEmail = e.target.value;
                    setCredentials({
                      ...credentials,
                      username: lowerCaseEmail,
                    });
                  }}
                  disabled={xhr === XHR_STATE.REQUESTING}
                  placeholder="chef@yourrestaurant.com"
                  required={true}
                />

                <LoginComponents.Icon>
                  <EmailIcon fontSize="small" />
                </LoginComponents.Icon>
              </LoginComponents.InputWrapper>
            </LoginComponents.FieldGroup>

            <LoginComponents.FieldGroup>
              <LoginComponents.FieldRow>
                <LoginComponents.Label htmlFor="password">
                  Password
                </LoginComponents.Label>
                <LoginComponents.TextLink as={Link} to="/reset-password">
                  Forgot password?
                </LoginComponents.TextLink>
              </LoginComponents.FieldRow>

              <LoginComponents.InputWrapper>
                <LoginComponents.InputField
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required={true}
                  onChange={(e) =>
                    setCredentials({
                      ...credentials,
                      password: e.target.value,
                    })
                  }
                  placeholder="••••••••••••"
                  disabled={xhr === XHR_STATE.REQUESTING}
                />

                <LoginComponents.Icon button>
                  <LoginComponents.IconButton
                    type="button"
                    disabled={xhr === XHR_STATE.REQUESTING}
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </LoginComponents.IconButton>
                </LoginComponents.Icon>
              </LoginComponents.InputWrapper>
            </LoginComponents.FieldGroup>

            <LoginComponents.SubmitButton
              type="submit"
              disabled={xhr === XHR_STATE.REQUESTING}
            >
              {xhr === XHR_STATE.REQUESTING ? (
                <>
                  <CircularProgress size={18} color="inherit" />
                  Authenticating...
                </>
              ) : (
                <>
                  Sign in
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </>
              )}
            </LoginComponents.SubmitButton>
          </LoginComponents.Form>
        </LoginComponents.FormShell>

        <LoginComponents.SecureNote>Secure access</LoginComponents.SecureNote>
      </LoginComponents.FormPanel>
    </LoginComponents.Container>
  );
};

export default Login;
